import { Popover } from '@mui/material';
import classNames from 'classnames';
import moment, { Moment } from 'moment-timezone';
import { path, pipe } from 'ramda';
import React, { useEffect, useRef, useState } from 'react';
import { Modifier } from 'react-day-picker';
import { useDispatch } from 'react-redux';
import { Actions as ReportActions } from '../../../../stores/reducers/report-reducers';
import CalendarSvg from '../../../SVG-components/calendar';
import CloseSvg from '../../../SVG-components/close';
import { DaysChooser, RangeDateChooser } from '../DateChoosers';
import styles from './DateFilterTabs.module.scss';

export enum DateChangeType {
  start = 'start',
  end = 'end',
  both = 'both'
}

type datesType = Array<{ startDate: moment.Moment | null, endDate: moment.Moment | null }>;
interface DateFilterTabsProps {
  dates: any[] | null | datesType;
  dateError?: string;
  type?: DateChangeType;
  onSaveDate?: () => void;
  submitBtnTitle?: string;
  dateUnstaged?: datesType;
  changeUnstagedDate?: (newDates: datesType) => void;
  handleChangeTimeEnd?: (endTime) => void;
}

const rangePickerStyle: React.CSSProperties = {
  flexDirection: 'column',
  alignItems: 'center',
}

const DATE_PRESETS = [
  { label: 'Today',        getRange: () => ({ startDate: moment().startOf('day'),                              endDate: moment().endOf('day') }) },
  { label: 'Yesterday',   getRange: () => ({ startDate: moment().subtract(1, 'day').startOf('day'),           endDate: moment().subtract(1, 'day').endOf('day') }) },
  { label: 'This Week',   getRange: () => ({ startDate: moment().startOf('week'),                             endDate: moment().endOf('week') }) },
  { label: 'Last Week',   getRange: () => ({ startDate: moment().subtract(1, 'week').startOf('week'),        endDate: moment().subtract(1, 'week').endOf('week') }) },
  { label: 'This Month',  getRange: () => ({ startDate: moment().startOf('month'),                            endDate: moment().endOf('month') }) },
  { label: 'Last Month',  getRange: () => ({ startDate: moment().subtract(1, 'month').startOf('month'),     endDate: moment().subtract(1, 'month').endOf('month') }) },
  { label: 'This Quarter',getRange: () => ({ startDate: moment().startOf('quarter'),                          endDate: moment().endOf('quarter') }) },
  { label: 'Last Quarter',getRange: () => ({ startDate: moment().subtract(1, 'quarter').startOf('quarter'), endDate: moment().subtract(1, 'quarter').endOf('quarter') }) },
];

const DateFilterTabs: React.FC<DateFilterTabsProps> = ({
  dates,
  dateError,
  type = DateChangeType.both,
  onSaveDate,
  submitBtnTitle = 'Update Report',
  changeUnstagedDate,
}) => {
  const [multiDate, setMultiDate] = useState<boolean>(true);
  const [inputValue, setInputValue] = useState<string>('');
  const [startOfDateChange, setStartOfDateChange] = useState<moment.Moment>();
  const [endOfDateChange, setEndOfDateChange] = useState<moment.Moment>();
  const [dateSelected, setDateSelected] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);

  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>();

  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    setAnchorEl(event.currentTarget);
    setDateSelected(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeStartDate = (startDate: moment.Moment) => {
    setStartOfDateChange(startDate);
    setDateSelected(true);
  }

  const handleChangeEndDate = (endDate: moment.Moment) => {
    setEndOfDateChange(endDate);
    setDateSelected(true);
  }

  const handleChangeDate = (selectedDate: moment.Moment, forwardType?: DateChangeType) => {
    if (forwardType === DateChangeType.start || type === DateChangeType.start) {
      handleChangeStartDate(selectedDate);
    }
    else if (forwardType === DateChangeType.end || type === DateChangeType.end) {
      handleChangeEndDate(selectedDate);
    }
  }

  // useEffect for closing the popover
  useEffect(() => {
    if (dateSelected && (startOfDateChange || endOfDateChange)) {
      if (changeUnstagedDate) {
        changeUnstagedDate([{
          startDate: (startOfDateChange || dates[0]?.startDate) ?? null,
          endDate: (endOfDateChange || dates[0]?.endDate) ?? null
        }]);
      }
      handleClose();
      setDateSelected(false);
    }
  }, [dateSelected, startOfDateChange, endOfDateChange, changeUnstagedDate, dates]);

  inputRef?.current?.blur();

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleApply = () => {
    if (startOfDateChange || endOfDateChange) {
      if (changeUnstagedDate) {
        changeUnstagedDate([{
          startDate: (startOfDateChange || dates[0]?.startDate) ?? null,
          endDate: (endOfDateChange || dates[0]?.endDate) ?? null
        }]);
      }
    }

    handleClose();
  }

  const handlePresetClick = (preset: typeof DATE_PRESETS[0]) => {
    const { startDate, endDate } = preset.getRange();
    if (changeUnstagedDate) {
      changeUnstagedDate([{ startDate, endDate }]);
    }
    handleClose();
  };

  const handleClearInput = () => {
    if (type === DateChangeType.start) {
      changeUnstagedDate([{
        startDate: null,
        endDate: (endOfDateChange || dates[0]?.endDate) ?? null
      }]);
    }
    else if (type === DateChangeType.end) {
      changeUnstagedDate([{
        startDate: (startOfDateChange || dates[0]?.startDate) ?? null,
        endDate: null
      }]);
    }
    setInputValue('');
  }

  useEffect(() => {
    if (dates && dates.length) {
      const formatInputValueBasedOnType = () => {
        const startDate = dates[0].startDate ? moment(dates[0].startDate).format('MM/DD/YYYY hh:mm A') : '';
        const endDate = dates[0].endDate ? moment(dates[0].endDate).format('MM/DD/YYYY hh:mm A') : '';

        switch (type) {
          case DateChangeType.start:
            return startDate;
          case DateChangeType.end:
            return endDate;
          case DateChangeType.both:
          default:
            return startDate && endDate ? `${startDate}-${endDate}` : startDate || endDate;
        }
      };

      const newInputValue = formatInputValueBasedOnType();
      setInputValue(newInputValue);
    } else {
      setInputValue('');
    }
  }, [dates, type]);

  const getDisabledDays = (
    startDate?: Moment,
    endDate?: Moment,
    pickerType?: DateChangeType,
  ): Modifier | undefined => {
    const today = new Date();

    if (pickerType === DateChangeType.start && endDate) {
      return { after: endDate.toDate()};
    }

    if (pickerType === DateChangeType.end && startDate) {
      return { before: startDate.clone().add(1, 'day').toDate(), after: today };
    }

    return {after: today};
  };

  const disabledDays = getDisabledDays(dates[0]?.startDate, dates[0]?.endDate, type);

  return (
    <section className={styles.datePickerContainer}>
      <div className={styles.dateInputWrapper}>
        <input
          className={classNames({ [styles.active]: anchorEl, [styles.error]: dateError })}
          ref={inputRef}
          onClick={handleClick}
          value={inputValue}
          readOnly
        />
        {inputValue ?
          <button onClick={handleClearInput} type='button' className={styles.closeButton}>
            <CloseSvg />
          </button> :
          <CalendarSvg className={styles.calendarSvg} />
        }
      </div>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div className={styles.pickerWrapper}>
          <div className={styles.presetsPanel}>
            {DATE_PRESETS.map((preset) => (
              <button key={preset.label} className={styles.presetButton} onClick={() => handlePresetClick(preset)}>
                {preset.label}
              </button>
            ))}
          </div>
          <div className={styles.pickerContent}>
            {dateError &&
              <div
                style={{
                  marginTop: 8,
                  color: 'darkred',
                  textAlign: 'center',
                  marginBottom: -25,
                  paddingBottom: 8
                }}
              >
                {dateError}
              </div>
            }

            {multiDate ? (
              <DaysChooser
                type={type}
                dates={dates}
                remove={pipe(ReportActions.REMOVE_DATE_GROUP_IN_DATES, dispatch)}
                dispatch={dispatch}
                onChange={handleChangeDate}
                timeOptionsClassName={styles.timeOptions}
                withTimeOptions={false}
                singleTimeOption
                disabledDays={disabledDays}
              />
            ) : (
              <RangeDateChooser
                style={dateError ? { borderColor: 'darkred' } : rangePickerStyle}
                initialStart={path([0, 'startDate'], dates)}
                initialEnd={path([0, 'endDate'], dates)}
                calendarHideable={false}
                calendarTop
                startDateChange={setStartOfDateChange}
                endDateChange={setEndOfDateChange}
                timeOptionsClassName={styles.timeOptions}
              />
            )}

            <div className={styles.controllPanel}>
              <button className={styles.cancelBtn} onClick={handleClose}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Popover>
    </section>
  );
};

export default DateFilterTabs;